import { Form, Formik } from 'formik'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import {
  convertAmountToPeriod,
  getItemAmountForPeriod,
} from '../../../lib/Calculator'
import { useFormatCurrency } from '../../../lib/CurrencyFormatter'
import { UserType } from '../../../lib/Enums/UserType'
import { Spend, SpendAmountCreate } from '../../../lib/Types'
import Distribution from '../../Distribution'
import { DistributionType } from '../lib/Enums/DistributionType'
import { SpendFrequency } from '../lib/Enums/SpendFrequency'
import { SpendType } from '../lib/Enums/SpendType'
import { useBudget } from '../lib/Hooks/Budget'
import { useUpdateSpend } from '../lib/Hooks/Spends'

interface Props {
  spend: Spend
  isSpendExpanded?: boolean
  types?: DistributionType[]
}

enum activeUser {
  NONE,
  PRIMARY,
  SECONDARY,
}

const getItemAmountForPeriodForUserPercentage = (
  spend: Spend,
  user: UserType,
): number => {
  const amount = getItemAmountForPeriod(spend, SpendFrequency.MONTHLY)

  const multiplier =
    user === UserType.PRIMARY
      ? spend.distribution_primary_user
      : 100 - spend.distribution_primary_user

  return (amount / 100) * multiplier
}

const getItemAmountForPeriodForUserCurrency = (
  spend: Spend,
  user: UserType,
): number => {
  const amount =
    user === UserType.PRIMARY
      ? spend.distribution_primary_user
      : spend.distribution_secondary_user

  return convertAmountToPeriod(
    { amount, frequency: spend.amounts.at(-1)!.frequency },
    SpendFrequency.MONTHLY,
  )
}

// distribution_primary_user/secondary_user are stored at the spend's own
// frequency (e.g. yearly), but the displayed amount - and what the user
// types while editing - is always the monthly-converted figure. Convert
// at the edit boundary so a yearly (or any non-monthly) spend edits the
// same way a monthly one does; percentages don't need this, they're
// frequency-agnostic already.
const toEditableAmount = (spend: Spend, distributionValue: number): number => {
  if (spend.distribution_type !== DistributionType.CURRENCY) {
    return distributionValue
  }

  return convertAmountToPeriod(
    { amount: distributionValue, frequency: spend.amounts.at(-1)!.frequency },
    SpendFrequency.MONTHLY,
  )
}

const fromEditableAmount = (
  spend: Spend,
  distributionType: DistributionType,
  editedAmount: number,
): number => {
  if (distributionType !== DistributionType.CURRENCY) {
    return editedAmount
  }

  return convertAmountToPeriod(
    { amount: editedAmount, frequency: SpendFrequency.MONTHLY },
    spend.amounts.at(-1)!.frequency!,
  )
}

const getCatoryAmountForPeriod = (spend: Spend, user: UserType): number => {
  let amount =
    spend.distribution_type === DistributionType.PERCENTAGE
      ? getItemAmountForPeriodForUserPercentage(spend, user)
      : getItemAmountForPeriodForUserCurrency(spend, user)

  if (spend.children) {
    spend.children.forEach((child) => {
      amount +=
        spend.distribution_type === DistributionType.PERCENTAGE
          ? getItemAmountForPeriodForUserPercentage(child, user)
          : getItemAmountForPeriodForUserCurrency(child, user)
    })
  }

  return amount
}

const SpendUserColumns: FC<Props> = ({
  spend,
  isSpendExpanded = false,
  types = [DistributionType.PERCENTAGE, DistributionType.CURRENCY],
}) => {
  const { t } = useTranslation(['budget', 'common'])
  const formatCurrency = useFormatCurrency()
  const { data: budget } = useBudget()
  const [isActive, setIsActive] = useState(activeUser.NONE)
  const { mutateAsync: updateSpend } = useUpdateSpend(budget!)

  const saveChangeForExpense = (
    distributionType: DistributionType,
    amount: number,
    user: UserType,
  ) => {
    let amountForPrimaryUser: number, amountForSecondaryUser: number

    if (distributionType === DistributionType.PERCENTAGE) {
      amountForPrimaryUser = user === UserType.PRIMARY ? amount : 100 - amount
      amountForSecondaryUser = user === UserType.PRIMARY ? 100 - amount : amount
    }

    if (distributionType === DistributionType.CURRENCY) {
      amountForPrimaryUser =
        user === UserType.PRIMARY
          ? amount
          : spend.amounts.at(-1)!.amount - amount
      amountForSecondaryUser =
        user === UserType.PRIMARY
          ? spend.amounts.at(-1)!.amount - amount
          : amount
    }

    // With no second user, there's nothing to redistribute against - a
    // currency edit here is the only way to change this spend's amount at
    // all, so it needs to update the real total instead of just shuffling
    // shares of the old one.
    if (!budget!.user && distributionType === DistributionType.CURRENCY) {
      const newAmounts = structuredClone(spend.amounts) as SpendAmountCreate[]
      newAmounts.push({
        formula: amount.toString(),
        frequency: spend.amounts.at(-1)!.frequency,
      })

      updateSpend({
        ...spend,
        distribution_type: distributionType,
        distribution_primary_user: amountForPrimaryUser!,
        distribution_secondary_user: amountForSecondaryUser!,
        amounts: newAmounts,
      })
      return
    }

    updateSpend({
      ...spend,
      distribution_type: distributionType,
      distribution_primary_user: amountForPrimaryUser!,
      distribution_secondary_user: amountForSecondaryUser!,
    })
  }

  const saveChangeForIncome = (amount: number, user: UserType) => {
    const distributionPrimaryUser =
      user === UserType.PRIMARY
        ? amount
        : spend.distribution_primary_user || 0
    const distributionSecondaryUser =
      user === UserType.SECONDARY
        ? amount
        : spend.distribution_secondary_user || 0

    // For a shared budget, each user's income is entered independently -
    // unlike expenses/savings there's no pre-existing total to keep the two
    // shares summing to. So the spend's real total (the household's actual
    // income) has to be derived as the sum of both, recomputed every time
    // either share changes, instead of being left at whatever it was when
    // the income was first added.
    if (budget!.user) {
      const newAmounts = structuredClone(spend.amounts) as SpendAmountCreate[]
      newAmounts.push({
        formula: `${distributionPrimaryUser}+${distributionSecondaryUser}`,
        frequency: spend.amounts.at(-1)!.frequency,
      })

      updateSpend({
        ...spend,
        amounts: newAmounts,
        distribution_primary_user: distributionPrimaryUser,
        distribution_secondary_user: distributionSecondaryUser,
      })
      return
    }

    updateSpend({
      ...spend,
      distribution_primary_user: distributionPrimaryUser,
      distribution_secondary_user: distributionSecondaryUser,
    })
  }

  const hasChildren = !!(spend.children && spend.children.length > 0)

  const getAmount = (userType: UserType) => {
    let amount = 0

    // A spend with children has no meaningful amount of its own: collapsed
    // it shows the sum of its children, expanded it shows 0 (the children's
    // own rows carry the real amounts).
    if (hasChildren) {
      amount = isSpendExpanded ? 0 : getCatoryAmountForPeriod(spend, userType)
    } else {
      amount =
        spend.distribution_type === DistributionType.PERCENTAGE
          ? getItemAmountForPeriodForUserPercentage(spend, userType)
          : getItemAmountForPeriodForUserCurrency(spend, userType)
    }

    return formatCurrency(amount, true, false)
  }

  return (
    <>
      <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
        {isActive === activeUser.PRIMARY && !hasChildren && (
          <Formik
            initialValues={{
              distribution_type: spend.distribution_type,
              distribution_primary_user: toEditableAmount(
                spend,
                spend.distribution_primary_user,
              ),
            }}
            validationSchema={Yup.object({
              distribution_type: Yup.string().required(
                t('common:validation.required'),
              ),
              distribution_primary_user: Yup.number().required(
                t('common:validation.required'),
              ),
            })}
            onSubmit={(values) => {
              const amount = fromEditableAmount(
                spend,
                values.distribution_type,
                values.distribution_primary_user,
              )

              spend.type === SpendType.INCOME
                ? saveChangeForIncome(amount, UserType.PRIMARY)
                : saveChangeForExpense(
                  values.distribution_type,
                  amount,
                  UserType.PRIMARY,
                )
              setIsActive(activeUser.NONE)
            }}
          >
            <Form>
              <Distribution
                nameInput='distribution_primary_user'
                types={types}
                max={
                  spend.type === SpendType.INCOME || !budget!.user
                    ? false
                    : getItemAmountForPeriod(spend, SpendFrequency.MONTHLY)
                }
              />
            </Form>
          </Formik>
        )}
        {(isActive !== activeUser.PRIMARY || hasChildren) && (
          <span
            className={hasChildren ? '' : 'cursor-pointer'}
            onClick={
              hasChildren ? undefined : () => setIsActive(activeUser.PRIMARY)
            }
          >
            {getAmount(UserType.PRIMARY)}
          </span>
        )}
      </td>
      {budget!.user && (
        <td className='text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap'>
          {isActive === activeUser.SECONDARY && !hasChildren && (
            <Formik
              initialValues={{
                distribution_type: spend.distribution_type,
                distribution_secondary_user: toEditableAmount(
                  spend,
                  spend.distribution_secondary_user,
                ),
              }}
              validationSchema={Yup.object({
                distribution_type: Yup.string().required(
                  t('common:validation.required'),
                ),
                distribution_secondary_user: Yup.number().required(
                  t('common:validation.required'),
                ),
              })}
              onSubmit={(values) => {
                const amount = fromEditableAmount(
                  spend,
                  values.distribution_type,
                  values.distribution_secondary_user,
                )

                spend.type === SpendType.INCOME
                  ? saveChangeForIncome(amount, UserType.SECONDARY)
                  : saveChangeForExpense(
                    values.distribution_type,
                    amount,
                    UserType.SECONDARY,
                  )
                setIsActive(activeUser.NONE)
              }}
            >
              <Form>
                <Distribution
                  nameInput='distribution_secondary_user'
                  types={types}
                  max={
                    spend.type === SpendType.INCOME || !budget!.user
                      ? false
                      : getItemAmountForPeriod(spend, SpendFrequency.MONTHLY)
                  }
                />
              </Form>
            </Formik>
          )}
          {(isActive !== activeUser.SECONDARY || hasChildren) && (
            <span
              className={hasChildren ? '' : 'cursor-pointer'}
              onClick={
                hasChildren
                  ? undefined
                  : () => setIsActive(activeUser.SECONDARY)
              }
            >
              {getAmount(UserType.SECONDARY)}
            </span>
          )}
        </td>
      )}
    </>
  )
}

export default SpendUserColumns
